import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useStockMedicineSearch } from "../../../../hooks/useStockMedicineSearch";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddPrescription = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const [extraMedicines, setExtraMedicines] = useState([]);
  const { data: doctors } = useFetchObjects(["doctors"], "users", token);

  // Use the new backend search hook for medicines
  const {
    suggestions: medicineSuggestions,
    isLoading: isSearchingMedicines,
    quickSearch: searchMedicines,
    clearResults: clearMedicineResults,
  } = useStockMedicineSearch();

  // Legacy medicine loading (fallback) - DISABLED: Now using backend search only
  // const { data: medicines } = useFetchObjects(
  //   ["stock-medicines"],
  //   `stock-medicines/`,
  //   token
  // );

  // Pagination and Search State for Patients
  const [patientPage, setPatientPage] = useState(1);
  const [patientPageSize] = useState(100);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [patientHasMore, setPatientHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [patientForceUpdate, setPatientForceUpdate] = useState(0);

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch: refetchPatients,
  } = useFetchObjects(
    [
      "patients",
      patientPage,
      patientPageSize,
      patientSearchTerm,
      patientForceUpdate,
    ],
    `patients/?page=${patientPage}&page_size=${patientPageSize}&search=${patientSearchTerm}`,
    token
  );

  // Added discount state to input object here
  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: "",
    patient: "",
    duration: "",
    discount: 0, // <--- added discount here
    total_price: 0,
    items: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const dosageRefs = useRef([]);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);

  const {
    handleAdd: handleAddPrescription,
    resetForm,
    loading,
  } = useAdd(
    "prescriptions",
    token,
    t("pharmacy.prescriptionCreatedSuccess"),
    t("pharmacy.prescriptionCreateFail")
  );

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: "",
        patient: "",
        duration: "",
        discount: 0, // reset discount too
        total_price: 0,
        items: [],
      });
      setFormErrors({});
      setAllPatients([]);
      setPatientPage(1);
      setPatientHasMore(true);
      setPatientInputValue("");
      setExtraMedicines([]);
    }
  }, [resetForm]);

  useEffect(() => {
    setInput((prev) => ({ ...prev, extra_medicines: extraMedicines }));
  }, [extraMedicines]);

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setPatientHasMore(patientsData.next !== null);
    } else {
      setPatientHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    if (!patientSearchTerm) {
      setAllPatients([]);
      setPatientPage(1);
      setPatientHasMore(true);
      refetchPatients();
    }
  }, [patientSearchTerm, refetchPatients]);

  const handleChangePrescriptionField = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  // New handler to handle discount changes and recalculate total price accordingly
  const handleDiscountChange = (e) => {
    const discountValue = parseInt(e.target.value) || 0;
    const newTotalPrice = Math.max(
      calculateTotalPrice(input.items) - discountValue,
      0
    );
    setInput((prev) => ({
      ...prev,
      discount: discountValue,
      total_price: newTotalPrice,
    }));
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      setInput((prevInput) => {
        const newItems = [
          ...prevInput.items,
          {
            stock_medicine: medicine.id,
            name: medicine.medicine_display,
            quantity: 1,
            dosage: 1,
            sell_price: medicine.sell_price,
            total_price: medicine.sell_price,
            available_stock: medicine.quantity,
            notation: "",
            is_before: "after",
            units_per_meal: 1,
          },
        ];
        // Calculate total price after adding new item, minus discount
        const total = Math.max(
          calculateTotalPrice(newItems) - (prevInput.discount || 0),
          0
        );
        return {
          ...prevInput,
          items: newItems,
          total_price: total,
        };
      });

      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    items[index][name] = value ? parseInt(value) || value : "";
    if (name === "quantity" || name === "sell_price") {
      items[index].total_price =
        items[index].sell_price * items[index].quantity;
    }
    // Recalculate total price minus discount
    const total = Math.max(
      calculateTotalPrice(items) - (input.discount || 0),
      0
    );
    setInput((prev) => ({ ...prev, items, total_price: total }));
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (field === "dosage" && unitsPerMealRefs.current[index]) {
        unitsPerMealRefs.current[index].focus();
      } else if (field === "units_per_meal" && quantityRefs.current[index]) {
        quantityRefs.current[index].focus();
      } else if (field === "quantity" && notationRefs.current[index]) {
        notationRefs.current[index].focus();
      } else if (field === "notation" && dosageRefs.current[index + 1]) {
        dosageRefs.current[index + 1].focus();
      }
    }
  };

  const addItems = () => {
    setExtraMedicines([
      ...extraMedicines,
      {
        title: "",
        dosage: "",
        quantity: "",
        notation: "",
        units_per_meal: "",
        is_before: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setExtraMedicines(extraMedicines?.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...extraMedicines];
    newItems[index][field] = value;
    setExtraMedicines(newItems);
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    const total = Math.max(
      calculateTotalPrice(items) - (input.discount || 0),
      0
    );
    setInput((prev) => ({ ...prev, items, total_price: total }));
  };

  const calculateTotalPrice = (items) =>
    items.reduce(
      (sum, item) => sum + (item.sell_price * item.quantity || 0),
      0
    );

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }
    if (!input.doctor) {
      errors.doctor = t("pharmacy.doctorRequired");
      isValid = false;
    }
    if (!input.patient) {
      errors.patient = t("pharmacy.patientRequired");
      isValid = false;
    }
    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
      if (!item.sell_price) {
        errors[`sell_price_${index}`] = t("pharmacy.priceRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      // Send discount and total_price in data
      const dataToSend = {
        ...input,
        total_price: input.total_price,
        discount: input.discount,
      };
      handleAddPrescription(dataToSend);
      setInput((prevInput) => ({
        ...prevInput,
        patient: "",
      }));
    }
  };

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetPatientSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setInput((prev) => ({ ...prev, patient: newValue ? newValue.id : "" }));
  };

  const handlePatientScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      patientHasMore &&
      !isLoadingPatients
    ) {
      setPatientPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced search terms
  const debouncedSetPatientSearchTerm = useCallback(
    debounce((value) => {
      setPatientSearchTerm(value);
    }, 300),
    []
  );

  // Debounced medicine search
  const debouncedMedicineSearch = useCallback(
    debounce((value) => {
      if (value && value.length >= 2) {
        searchMedicines(value, 50);
      } else {
        clearMedicineResults();
      }
    }, 300),
    [searchMedicines, clearMedicineResults]
  );

  // Force update functions
  const forceUpdatePatientAutocomplete = () => {
    setPatientForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (patientSearchTerm === "") {
      forceUpdatePatientAutocomplete();
    }
  }, [patientSearchTerm]);

  return (
    <Card
      sx={{
        boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
        borderRadius: 4,
        backgroundColor: "#f4f6f8",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("pharmacy.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("pharmacy.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("pharmacy.addPrescriptionTitle")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.prescriptionDate")}
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={input.date}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.date}
                helperText={formErrors.date}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.time")}
                name="time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={input.time}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.time}
                helperText={formErrors.time}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={allPatients}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname || ""} (ID: ${
                    option.id
                  }, Phone: ${option.phone || "N/A"})`
                }
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                ListboxProps={{
                  onScroll: handlePatientScroll,
                }}
                loading={isLoadingPatients}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography>
                      {option.firstname} {option.lastname || ""} (ID:{" "}
                      {option.id}, Phone: {option.phone || "N/A"})
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("pharmacy.patient")}
                    variant="outlined"
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingPatients ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                name="doctor"
                variant="outlined"
                label={t("pharmacy.selectDoctor")}
                value={input.doctor}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.doctor}
                helperText={formErrors.doctor}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              >
                {doctors?.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.first_name} {doctor.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.duration")}
                name="duration"
                variant="outlined"
                value={input.duration}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.duration}
                helperText={formErrors.duration}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Search Medicine Section */}
        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.addMedicine")}
          </Typography>
          <Autocomplete
            ref={autocompleteRef}
            value={null}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
              // Clear previous results immediately when user types
              if (newInputValue.length < 2) {
                clearMedicineResults();
              }
              // Trigger backend search
              debouncedMedicineSearch(newInputValue);
            }}
            onChange={(event, value) => {
              handleAddNewItem(value);
              setAutocompleteInputValue("");
              clearMedicineResults();
            }}
            options={
              autocompleteInputValue.length >= 2
                ? medicineSuggestions || []
                : []
            }
            loading={isSearchingMedicines}
            filterOptions={(x) => x}
            getOptionLabel={(option) =>
              `${option.medicine_display} (${option.category_display})`
            }
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#eef2f5",
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", color: "text.primary" }}
                >
                  {option.medicine_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.category")}:</strong>{" "}
                  {option.category_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                  {option.expiry_date}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.searchMedicine")}
                variant="outlined"
                placeholder={t("pharmacy.searchMedicinePlaceholder")}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {isSearchingMedicines ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
              />
            )}
            noOptionsText={
              isSearchingMedicines
                ? t("pharmacy.searching")
                : autocompleteInputValue.length >= 2
                ? t("pharmacy.noMedicinesFound")
                : t("pharmacy.typeToSearch")
            }
          />
        </Box>

        {/* Prescription Items Table */}
        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("pharmacy.medicineName")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.availableStock")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.dosage")}</TableCell>
                <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                  {t("pharmacy.unitsPerMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                <TableCell align="center">{t("pharmacy.price")}</TableCell>
                <TableCell align="center">{t("pharmacy.notation")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.isBeforeMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
                <TableCell align="center">{t("pharmacy.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {input.items.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{item.available_stock}</TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (dosageRefs.current[index] = el)}
                      name="dosage"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.dosage}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "dosage")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (unitsPerMealRefs.current[index] = el)}
                      name="units_per_meal"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.units_per_meal}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) =>
                        handleKeyDown(e, index, "units_per_meal")
                      }
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (quantityRefs.current[index] = el)}
                      name="quantity"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.quantity}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "quantity")}
                      error={!!formErrors[`quantity_${index}`]}
                      helperText={formErrors[`quantity_${index}`]}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (priceRefs.current[index] = el)}
                      name="sell_price"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.sell_price}
                      onChange={(e) => handleChangeItemField(index, e)}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      disabled
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (notationRefs.current[index] = el)}
                      name="notation"
                      type="text"
                      size="small"
                      variant="outlined"
                      value={item.notation}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "notation")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl fullWidth size="small">
                      <Select
                        value={item.is_before}
                        onChange={(e) => handleChangeItemField(index, e)}
                        name="is_before"
                        ref={(el) => (isBeforeRefs.current[index] = el)}
                      >
                        <MenuItem value="before">
                          {t("pharmacy.before")}
                        </MenuItem>
                        <MenuItem value="med">{t("pharmacy.med")}</MenuItem>
                        <MenuItem value="after">{t("pharmacy.after")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={600}>{item.total_price}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("pharmacy.removeItem")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container>
          {/* Add Extra Medicines */}
          {extraMedicines?.map((items, index) => {
            return (
              <Grid
                container
                justifyContent={"space-between"}
                gap={2}
                mt={2}
                key={index}
              >
                <TextField
                  name="title"
                  label={t("pharmacy.medicineName")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="quantity"
                  label={t("pharmacy.quantity")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="dosage"
                  label={t("pharmacy.dosage")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.dosage}
                  onChange={(e) =>
                    handleItemChange(index, "dosage", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="notation"
                  label={t("pharmacy.notation")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.notation}
                  onChange={(e) =>
                    handleItemChange(index, "notation", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="units_per_meal"
                  label={t("pharmacy.unitsPerMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.units_per_meal}
                  onChange={(e) =>
                    handleItemChange(index, "units_per_meal", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="is_before"
                  label={t("pharmacy.isBeforeMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.is_before}
                  onChange={(e) =>
                    handleItemChange(index, "is_before", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutline />
                </IconButton>
              </Grid>
            );
          })}
        </Grid>

        <Button variant="outlined" onClick={addItems} sx={{ mt: 1 }}>
          {t("pharmacy.addExtraMedicines", "Add Extra Medicines")}
        </Button>

        {/* Save Button and Total Price Display */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <TextField
            size="small"
            label={t("pharmacy.totalPrice")}
            type="number"
            value={input.total_price}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
            disabled
          />
          <TextField
            size="small"
            label={t("pharmacy.discount", "Discount")}
            type="number"
            name="discount"
            value={input.discount || 0}
            onChange={handleDiscountChange}
            InputProps={{
              inputProps: { min: 0 },
            }}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
          />
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 700,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddPrescription;
